import { ReactNode, useEffect } from 'react';

export interface ModalWrapperProps {
  isOpen?: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
}

const sizeClasses = {
  sm: 'w-full sm:max-w-sm',
  md: 'w-full sm:max-w-md',
  lg: 'w-full md:max-w-lg',
  xl: 'w-full md:max-w-xl',
  '2xl': 'w-full lg:max-w-2xl',
};

/**
 * Base modal wrapper component with:
 * - Fixed overlay with backdrop
 * - Configurable size
 * - Click-outside closing
 * - ESC key closing
 * - z-index management
 */
export function ModalWrapper({
  isOpen = true,
  onClose,
  children,
  className = '',
  size = 'md',
  closeOnClickOutside = true,
  closeOnEscape = true,
}: ModalWrapperProps) {
  // Handle ESC key press
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      data-tsx-id="modal-wrapper"
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={(e) => {
        // Only close if clicking the overlay itself, not its children
        if (e.target === e.currentTarget && closeOnClickOutside) {
          onClose();
        }
      }}
    >
      <div 
        className={`bg-white rounded-lg shadow-xl max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto ${sizeClasses[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
